import { HttpClient } from '@angular/common/http';
import {
  DestroyRef,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { catchError, filter, from, Observable, of, switchMap, tap } from 'rxjs';
import {
  Channel,
  SocketService,
} from 'src/app/shared/components/in-progress-processes/socket.service';
import {
  IInProgressUpload,
  IInsuranceIntelligence,
  ISmartUploadProcess,
  SmartUploadProcessStatus,
} from './submission-link-upload.models';
import { DirectUpload } from '@rails/activestorage';

@Injectable({
  providedIn: 'root',
})
export class SubmissionLinkUploadService {
  smartUploadProcesses: WritableSignal<ISmartUploadProcess[]> = signal([]);
  isUploading: WritableSignal<boolean> = signal(false);
  private _http = inject(HttpClient);
  private _socketService = inject(SocketService);

  private readonly supportedUploadStatuses = new Set<SmartUploadProcessStatus>([
    SmartUploadProcessStatus.Enriching,
    SmartUploadProcessStatus.Completed,
    SmartUploadProcessStatus.Failed,
  ]);

  checkForInProgressUploads(destroyRef: DestroyRef): void {
    const url = `${environment.apiBaseUri}/universal_submit_upload?status=InProgress`;

    this._http
      .get<IInProgressUpload[]>(url)
      .pipe(
        catchError(() => of<IInProgressUpload[]>([])),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((uploads) => {
        const trackedUploadIds = new Set(
          this.smartUploadProcesses().map(({ upload_id }) => upload_id),
        );

        const untrackedUploads = uploads.filter(
          ({ id }) => !trackedUploadIds.has(id),
        );

        if (!untrackedUploads.length) return;

        this.smartUploadProcesses.update((processes) => [
          ...processes,
          ...untrackedUploads.map((upload) => ({
            upload_id: upload.id,
            filenames: upload.files,
            application_id: null,
            status: this.normalizeUploadStatus(upload.status),
          })),
        ]);

        untrackedUploads.forEach(({ id }) => this.startWatchingUpload(id));
      });
  }

  uploadDocuments(files: File[]): Observable<{ upload_id: string }> {
    const url = `${environment.apiBaseUri}/universal_submit_upload`;
    const formData = new FormData();

    files.forEach((file) => formData.append('files[]', file, file.name));

    return this._http.post<{ upload_id: string }>(url, formData).pipe(
      filter((response) => !!response.upload_id),
      tap(({ upload_id }) => {
        this.smartUploadProcesses.update((processes) => [
          ...processes,
          {
            upload_id,
            filenames: files.map((file) => file.name),
            application_id: null,
            status: SmartUploadProcessStatus.Extracting,
          },
        ]);

        this.startWatchingUpload(upload_id);
      }),
    );
  }

  directUpload(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const url = `${environment.apiBaseUri}/rails/active_storage/direct_uploads`;
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.withCredentials = true;
        },
      });

      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve(blob?.signed_id ?? '');
        }
      });
    });
  }

  uploadAndSave(files: File[]): Observable<any> {
    return from(Promise.all(files.map((file) => this.directUpload(file)))).pipe(
      switchMap((signedIds) => this.saveSignedIds(signedIds)),
    );
  }

  getDocument(documentUrl: string) {
    const url = `${environment.apiBaseUri}${documentUrl}`;
    return this._http.get(url, { responseType: 'blob' });
  }

  getDocuments(documentUrls: string[]): Observable<Blob>[] {
    return documentUrls.map((url) => this.getDocument(url));
  }

  private startWatchingUpload(uploadId: string): void {
    this._socketService.subscribe(Channel.UniversalSubmit, uploadId, (data) =>
      this.receivedUniversalSubmitUpdate(data, uploadId),
    );
  }

  private receivedUniversalSubmitUpdate(
    data: IInsuranceIntelligence,
    uploadId: string,
  ): void {
    const status = this.normalizeUploadStatus(data.submission_status);
    this.smartUploadProcesses.update((processes) =>
      processes.map((process) =>
        process.upload_id === uploadId
          ? {
              ...process,
              application_id: data.quote_application?.application_id ?? null,
              status,
            }
          : process,
      ),
    );

    if (
      status === SmartUploadProcessStatus.Completed ||
      status === SmartUploadProcessStatus.Failed
    ) {
      this._socketService.unsubscribeFromChannel(
        Channel.UniversalSubmit,
        uploadId,
      );
    }
  }

  private isSupportedUploadStatus(
    status: string,
  ): status is SmartUploadProcessStatus {
    return this.supportedUploadStatuses.has(status as SmartUploadProcessStatus);
  }

  private normalizeUploadStatus(status: string): SmartUploadProcessStatus {
    if (status === SmartUploadProcessStatus.Processing) {
      return SmartUploadProcessStatus.Enriching;
    }
    return this.isSupportedUploadStatus(status)
      ? status
      : SmartUploadProcessStatus.Extracting;
  }

  private saveSignedIds(signedIds: string[]): Observable<any> {
    const url = `${environment.apiBaseUri}/universal_submit_upload/create_from_client_uploads`;

    return this._http
      .post(url, {
        documents: signedIds,
      })
      .pipe(
        filter((response: { upload_id: string }) => !!response.upload_id),
        tap(({ upload_id, files }) => {
          this.smartUploadProcesses.update((processes) => [
            ...processes,
            {
              upload_id,
              filenames: files.map((file) => file.filename),
              application_id: null,
              status: SmartUploadProcessStatus.Extracting,
            },
          ]);

          this.startWatchingUpload(upload_id);
        }),
      );
  }
}
